import React, {
  useEffect,
} from 'react';
import queryString from 'query-string';

import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import { Typography } from '@waglabs/typography';
import {
  AppDownloadButton,
  Distribution,
} from '@waglabs/get-app-button';

import Tracker from '../../../../components/Tracker';
import { Container } from '../../../../components/Container';
import SignInHeaderBackground from '../../../../assets/images/sign-in-header-background.jpg';
import { Card } from '../../../../components/Card/Card';
import { CardContent } from '../../../../components/CardContent/CardContent';
import { WagLoginForm } from './components/WagLoginFormV2';
import LoadingBoneModal from '../../../OwnerRegistration/components/LoadingBoneOverlay';
import { LoginByEmailFormStepper } from './components/WagLoginFormV2/LoginByEmailFormStepper';
import { LoginByPhoneFormStepper } from './components/WagLoginFormV2/LoginByPhoneFormStepper';

import { useGetAuthenticationSession } from '../../../../hooks/api/useGetAuthenticationSession';
import {
  useCreateOwnerPreferredWalkerByPromoCode,
} from '../../../../hooks/api/useCreateOwnerPreferredWalkerByPromoCode';
import { useSession } from '../../../../hooks';

import {
  withHistory,
  WithHistoryInjectedProps,
} from '../../../../hocs/withHistory';

import {
  Auth,
  QS_PROMO_CODE_KEY,
  QS_REDIRECT_URL_KEY, ROUTE_HOME,
  AppDownloadLink,
} from '../../../../constants';

import Logo from '../../../../assets/svg/Logo';

type WagLoginProps = WithHistoryInjectedProps;

const formStepperMapping = {
  [Auth.AllowedMethods.Email]: LoginByEmailFormStepper.name,
  [Auth.AllowedMethods.Phone]: LoginByPhoneFormStepper.name,
};

const getFormStepperFlow = data => (
  // Get the first item on the array as the form stepper flow
  (Array.isArray(data) && data.slice(0, 1))
    || formStepperMapping[Auth.AllowedMethods.Email]
);

const handleRedirectOnComplete = ({
  search,
  push,
}) => {
  const {
    [QS_REDIRECT_URL_KEY]: redirectUrlQueryString,
    [QS_PROMO_CODE_KEY]: promoCode,
    ...otherQueryString
  } = queryString.parse(search);
  const queryStringToForward = queryString.stringify(otherQueryString);
  const redirectUrl = redirectUrlQueryString || ROUTE_HOME;

  push({
    path: redirectUrl,
    search: queryStringToForward,
    retainQueryString: false,
  });
};

const handleOnFormStepperComplete = ({
  search,
  push,
  createOwnerPreferredWalkerByPromoCode,
  clearSession,
}) => () => {
  const {
    [QS_PROMO_CODE_KEY]: promoCode,
  } = queryString.parse(search);

  if (promoCode) {
    createOwnerPreferredWalkerByPromoCode({
      walkerPromoCode: promoCode,
    });
  }

  clearSession();

  handleRedirectOnComplete({
    search,
    push,
  });
};

const WagLogin = ({
  history: {
    location: {
      search,
    },
    push,
  },
}: WagLoginProps) => {
  const {
    data,
    execute: getAuthenticationSession,
    isSuccess: isGetAuthenticationSuccess,
    isLoading: isGetAuthenticationLoading,
  } = useGetAuthenticationSession();
  const {
    execute: createOwnerPreferredWalkerByPromoCode,
    isLoading: isCreateOwnerPreferredWalkerByPromoCodeLoading,
  } = useCreateOwnerPreferredWalkerByPromoCode();

  const {
    clear: clearSession,
  } = useSession();

  const formStepperName = getFormStepperFlow(data);

  useEffect(() => {
    if (isGetAuthenticationSuccess) {
      return;
    }
    getAuthenticationSession();
  }, [
    isGetAuthenticationSuccess,
    getAuthenticationSession,
  ]);

  if (isGetAuthenticationLoading
      || isCreateOwnerPreferredWalkerByPromoCodeLoading
  ) {
    return <LoadingBoneModal />;
  }

  return (
    <>
      <Container
        px={[0, '16px', '24px']}
      >
        <Flex
          backgroundImage={[
            `url(${SignInHeaderBackground})`,
          ]}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition={['unset', 'center']}
          height={['344px']}
          flexDirection="column"
          justifyContent="flex-end"
          p="48px"
        >
          <Logo
            height="64px"
          />
        </Flex>
        <Flex
          justifyContent="center"
          textAlign="center"
          mx="16px"
          mt="-16px"
        >
          <Box
            width={['448px']}
          >
            <Typography
              variant="h2"
              fontWeight="700"
            >
              Sign in
            </Typography>
            <Typography>
              Sign in to access your Wag! account.
            </Typography>
            <Card
              backgroundColor="#EDEDED"
              borderRadius="12px"
              border="none"
            >
              <CardContent
                width={1}
                p="24px"
              >
                <WagLoginForm
                  formStepperName={formStepperName}
                  onComplete={handleOnFormStepperComplete({
                    createOwnerPreferredWalkerByPromoCode,
                    push,
                    search,
                    clearSession,
                  })}
                />
              </CardContent>
            </Card>
          </Box>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="center"
        >
          <Typography
            my="16px"
          >
            Download the Wag! app
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={['1fr', 'repeat(2, 1fr)']}
            justifyItems="center"
            alignItems="center"
            gridGap="16px"
            mb={[4, 4, 8]}
            width={[1, '416px']}
            mx={[null, 'auto']}
          >
            <AppDownloadButton
              distribution={Distribution.AppStore}
              display="inline-block"
              href={AppDownloadLink.Login.AppStore}
              target="_self"
              width={['200px', 1]}
            />

            <AppDownloadButton
              distribution={Distribution.PlayStore}
              display="inline-block"
              href={AppDownloadLink.Login.PlayStore}
              target="_self"
              width={['200px', 1]}
            />
          </Box>
        </Flex>
      </Container>
      <Tracker/>
    </>
  );
};

const EnhancedWagLogin = withHistory(WagLogin);

export {
  EnhancedWagLogin as WagLogin,
};
